import { FlowIllustration, type GuidedFlowKey } from './illustrations.js';
import './guided_flow_cards.css';

/**
 * `GuidedFlowCards` — os atalhos ILUSTRADOS de reserva manual (handoff
 * "GuidedFlowCards"): rótulo + fileira de cards ilustrados, um por serviço.
 * FONTE ÚNICA das duas superfícies: a home do web-chat e o card `guided_flows`
 * na conversa. Muda só `size`/`labelAlign`/`stroke` — nunca um fork por tela.
 *
 * Apresentacional puro: recebe os rótulos já localizados (cada superfície é dona
 * da sua copy) e devolve a escolha por `onSelect`. Sem IO, sem i18n aqui.
 *
 * O rótulo do card vive DENTRO do `<button>` (e não como irmão dele) para que o
 * nome acessível do controle seja o nome do serviço — a ilustração é decorativa.
 * A fileira acompanha o `labelAlign` (regra 2 do handoff: rótulo e cards nunca
 * desencontrados).
 */
/* A medida do card por degrau, como utilitária no elemento (regra do kit: layout de
 * elemento sob `.genui2` mora na utilitária, não na folha legada). `large` é o estádio
 * ~1.9:1 do mockup "Home (Composer)" — 4 pills + 3 gaps de 20px = 640px — e encolhe
 * abaixo da régua `wide` (880) para 156×72 (2×156 + gap 14 = 326 ≤ 343px úteis num viewport de 375).
 * `small` NÃO muda no mobile: é o acessório da conversa (#2670). `icon` é a home sem
 * viagem (#2769): a arte solta num quadrado 56 de raio 16 — `rounded-[16px]` porque
 * `rounded-2xl` no tema do ui-kit é 18px. `box-border` porque a home monta FORA do
 * `.genui2` (sem o preflight de border-box do DS) e a hairline de 1px somava 58×58 —
 * assim fecha em 56 em qualquer host. */
const CARD_SIZE = {
  large: 'h-19 w-[145px] max-wide:h-18 max-wide:w-39',
  small: 'h-14 w-26',
  icon: 'size-14 rounded-[16px] box-border',
} as const;

/* Os espaçamentos do degrau `icon` (frame: 32 entre atalhos, 8 arte→rótulo, 16
 * caption→fileira), também como utilitária — vencem os valores da folha legada
 * (20/10/14, e os 14px da fileira no mobile). Vazios nos outros degraus. */
const ICON_SPACING = {
  row: 'gap-8',
  item: 'gap-2',
  label: 'mb-4',
} as const;

export function GuidedFlowCards<K extends GuidedFlowKey>({
  flows,
  onSelect,
  label,
  labelAlign = 'left',
  size = 'large',
  stroke = true,
  animateIn = false,
  disabled = false,
}: {
  readonly flows: readonly { key: K; label: string }[];
  readonly onSelect: (flow: K) => void;
  /** Rótulo acima da fileira. Ausente = só os cards. */
  readonly label?: string;
  readonly labelAlign?: 'left' | 'center' | 'right';
  /** Pill do card: 145×76 (`large`), 104×56 (`small`) ou a arte solta em 56×56 de raio
   *  16 (`icon`, a home sem viagem #2769). Só o `large` muda no mobile — ver `CARD_SIZE`. */
  readonly size?: 'small' | 'large' | 'icon';
  /** Hairline em repouso. Ligado por default: home e thread são superfícies
   *  brancas sem delimitação própria (regra 4 do handoff). */
  readonly stroke?: boolean;
  /** Entrada escalonada (respeita `prefers-reduced-motion` no CSS). */
  readonly animateIn?: boolean;
  readonly disabled?: boolean;
}) {
  if (flows.length === 0) return null;
  const icon = size === 'icon';
  const cls = [
    'gfc',
    `gfc--${labelAlign}`,
    stroke ? 'gfc--stroke' : '',
    animateIn ? 'gfc--in' : '',
    icon ? 'gfc--icon' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const spacing = icon ? ICON_SPACING : { row: '', item: '', label: '' };

  return (
    <div className={cls}>
      {label && <span className={`gfc-label ${spacing.label}`.trim()}>{label}</span>}
      <div className={`gfc-row ${spacing.row}`.trim()}>
        {flows.map((flow, i) => (
          <button
            type="button"
            key={flow.key}
            className={`gfc-item ${spacing.item}`.trim()}
            disabled={disabled}
            style={animateIn ? { animationDelay: `${String(i * 130)}ms` } : undefined}
            onClick={() => {
              onSelect(flow.key);
            }}
          >
            <span className={`gfc-card ${CARD_SIZE[size]}`}>
              <FlowIllustration flow={flow.key} />
            </span>
            <span className="gfc-cap">{flow.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
